import { apiFetch } from '@/utils/api';

const V2 = (endpoint: string, options?: RequestInit) =>
    apiFetch(`/api/v2${endpoint}`, options);

const json = async <T = any>(p: Promise<Response>): Promise<T> => {
    const res = await p;
    return res.json();
};

export const v2Api = {
    // ─── Identity ───
    sync: () => json(v2Api.post('/users/sync')),
    summary: (userId: string | number) => json(v2Api.get(`/users/${userId}/summary`)),
    stats: (userId: string | number) => json(v2Api.get(`/users/${userId}/stats`)),
    rating: (userId: string | number) => json(v2Api.get(`/users/${userId}/rating`)),
    assets: (userId: string | number) => json(v2Api.get(`/users/${userId}/assets`)),
    orders: (userId: string | number, role: 'buyer' | 'seller' = 'buyer', status?: string) =>
        json(v2Api.get(`/users/${userId}/orders?role=${role}${status ? `&status=${status}` : ''}`)),
    bids: (userId: string | number) => json(v2Api.get(`/users/${userId}/bids`)),
    transactions: (userId: string | number) => json(v2Api.get(`/users/${userId}/transactions`)),
    ownershipHistory: (userId: string | number) => json(v2Api.get(`/users/${userId}/ownership-history`)),
    recomputeStats: (userId: string | number) => json(v2Api.post(`/users/${userId}/recompute-stats`)),

    // ─── Wallet ───
    walletBalance: (userId: string | number) => json(v2Api.get(`/wallet/${userId}/balance`)),
    walletLedger: (userId: string | number, limit = 50) =>
        json(v2Api.get(`/wallet/${userId}/ledger?limit=${limit}`)),
    walletReservations: (userId: string | number, status?: string) =>
        json(v2Api.get(`/wallet/${userId}/reservations${status ? `?status=${status}` : ''}`)),
    deposit: (body: { amount: number; fee?: number; paymentMethod?: string; externalReference?: string | null }) =>
        json<any>(apiFetch('/api/v2/wallet/deposit', { method: 'POST', body: JSON.stringify(body) })),

    // ─── NFT wallet ───
    nftWallet: (userId: string | number) => json(v2Api.get(`/nft/wallet/${userId}`)),
    depositNft: (nftFields: any) => json(v2Api.post('/nft/deposit', JSON.stringify(nftFields))),
    withdrawNft: (body: { assetId: number; destinationWallet: string }) =>
        json(v2Api.post('/nft/withdraw', JSON.stringify(body))),

    // ─── Store / Listings ───
    listings: (params?: { type?: string; status?: string }) => {
        const q = new URLSearchParams();
        if (params?.type && params.type !== 'all') q.set('type', params.type);
        if (params?.status && params.status !== 'all') q.set('status', params.status);
        const qs = q.toString();
        return json(v2Api.get(`/listings${qs ? `?${qs}` : ''}`));
    },
    listing: (id: string | number) => json(v2Api.get(`/listings/${id}`)),
    userListings: (userId: string | number) => json(v2Api.get(`/listings/user/${userId}`)),
    createListing: (body: any) => json(v2Api.post('/listings', JSON.stringify(body))),
    createUsernameListing: (body: { username: string; listingType: 'store' | 'auction' | 'offer'; price: number; durationHours?: number; minIncrement?: number }) =>
        json(v2Api.post('/usernames/list', JSON.stringify(body))),
    // Direct ownership registration – saves to user_nfts inventory without a marketplace listing
    registerOwnership: (username: string) =>
        json(v2Api.post('/ownership/register', JSON.stringify({ username }))),
    deleteOwnership: (body: { id?: number; nft_address?: string; nft_name?: string }) =>
        json(v2Api.post('/ownership/delete', JSON.stringify(body))),
    purchase: (id: string | number, idempotencyKey?: string) =>
        json(v2Api.post(`/listings/${id}/purchase`, JSON.stringify({ idempotencyKey }))),
    deactivate: (id: string | number) =>
        json(v2Api.post(`/listings/${id}/deactivate`, JSON.stringify({}))),
    // Cancel an existing listing or change its price (managed by username).
    // Auction locks once a bid lands.
    manageListing: (username: string, body: { action: 'cancel' | 'price'; price?: number }) =>
        json(v2Api.post(`/usernames/${encodeURIComponent(username)}/manage`, JSON.stringify(body))),

    // ─── Auctions ───
    auctions: (params?: { status?: string; include?: boolean }) => {
        const q = new URLSearchParams();
        if (params?.status && params.status !== 'all') q.set('status', params.status);
        if (params?.include) q.set('include', '1');
        const qs = q.toString();
        return json(v2Api.get(`/auctions${qs ? `?${qs}` : ''}`));
    },
    auction: (id: string | number) => json(v2Api.get(`/auctions/${id}`)),
    placeBid: (id: string | number, body: { amount: number; idempotencyKey?: string }) =>
        json(v2Api.post(`/auctions/${id}/bid`, JSON.stringify(body))),

    // ─── Offers ───
    userOffers: (userId: string | number, direction: 'received' | 'sent') =>
        json(v2Api.get(`/offers/user/${userId}?direction=${direction}`)),
    createOffer: (body: { listingId: number; assetId: number; sellerId: number; amount: number; expiresAt?: string | null }) =>
        json(v2Api.post('/offers', JSON.stringify(body))),
    acceptOffer: (id: number, counterAmount?: number | null) =>
        json(v2Api.post(`/offers/${id}/accept`, JSON.stringify({ counterAmount: counterAmount ?? null }))),
    rejectOffer: (id: number) => json(v2Api.post(`/offers/${id}/reject`, JSON.stringify({}))),
    cancelOffer: (id: number) => json(v2Api.post(`/offers/${id}/cancel`, JSON.stringify({}))),

    // ─── GRAM Staking ───
    // Staked balance is reserved on the wallet; rewards accrue lazily on read.
    gramStakingSummary: () => json(v2Api.get('/staking/gram/summary')),
    gramStakingHistory: (limit = 20) => json(v2Api.get(`/staking/gram/history?limit=${limit}`)),
    gramStake: (amount: number) =>
        json(apiFetch('/api/v2/staking/gram/stake', { method: 'POST', body: JSON.stringify({ amount }) })),
    gramUnstake: (amount?: number) =>
        json(
            apiFetch('/api/v2/staking/gram/unstake', {
                method: 'POST',
                body: JSON.stringify(amount == null ? {} : { amount }),
            })
        ),
    gramClaim: () => json(apiFetch('/api/v2/staking/gram/claim', { method: 'POST', body: JSON.stringify({}) })),

    // ─── P2P ───
    p2pDetails: (id: string | number) => json(v2Api.get(`/p2p/${id}`)),

    // ─── Reviews ───
    submitReview: (body: { orderId: number; reviewedUserId: number; rating: number; comment?: string | null }) =>
        json(v2Api.post('/reviews', JSON.stringify(body))),

    get: (endpoint: string) => V2(endpoint),
    post: (endpoint: string, body?: string) =>
        apiFetch(`/api/v2${endpoint}`, body ? { method: 'POST', body } : { method: 'POST' }),
};

export default v2Api;